export class IVFAPI {
  constructor(apiClient) {
    this.apiClient = apiClient;
  }

  async searchPatients(searchTerm, limit = 10, offset = 0) {
    return this.apiClient.post('/api/v1/ivf/search-patients', {
      searchTerm,
      limit,
      offset,
    });
  }

  async getProcedureTypes() {
    return this.apiClient.get('/api/v1/ivf/procedure-types');
  }

  async createDraftProcedure(contactId, procedureType, procedureSubType = 'SELF') {
    return this.apiClient.post('/api/v1/ivf/procedures/draft/create', {
      irm_contacts_id: contactId,
      procedure_type: procedureType,
      procedure_sub_type: procedureSubType,
    });
  }

  async getProceduresByContact(contactId) {
    return this.apiClient.get(`/api/v1/ivf/procedures/${contactId}`);
  }

  async getDraftProcedures(contactId) {
    return this.apiClient.get(`/api/v1/ivf/procedures/${contactId}/draft`);
  }

  async getProcedureById(procedureId) {
    return this.apiClient.get(`/api/v1/ivf/procedure/${procedureId}`);
  }

  async getInitiationData(procedureId) {
    return this.apiClient.get(`/api/v1/ivf/procedure/${procedureId}/initiation-data`);
  }

  // Full save/edit/delete change history for a patient's IVF Lab Performance data
  async getContactLabPerformanceAuditLogs(contactId, page = 1, limit = 15) {
    return this.apiClient.get(`/api/v1/ivf/contact/${contactId}/lab-performance-audit-logs?page=${page}&limit=${limit}`);
  }

  // Full save/edit/delete change history for a patient's IVF Procedure data
  // (Procedure, Folliculometry, Oocyte Collection, Embryo Transfer/Outcome/
  // FET Monitoring, Donor, History, USG) — everything under IVF except Lab
  // Performance, which has its own source above.
  async getContactProcedureAuditLogs(contactId, page = 1, limit = 15) {
    return this.apiClient.get(`/api/v1/ivf/contact/${contactId}/procedure-audit-logs?page=${page}&limit=${limit}`);
  }

  async saveProcedureContact(procedureId, contactData) {
    return this.apiClient.post(`/api/v1/ivf/procedure/${procedureId}/contacts`, contactData);
  }

  async getProcedureContacts(procedureId) {
    return this.apiClient.get(`/api/v1/ivf/procedure/${procedureId}/contacts`);
  }

  async saveProcedureHistories(procedureId, historiesData) {
    return this.apiClient.post(`/api/v1/ivf/procedure/${procedureId}/histories`, historiesData);
  }

  async getProcedureHistories(procedureId) {
    return this.apiClient.get(`/api/v1/ivf/procedure/${procedureId}/histories`);
  }

  async createProcedureHistory(data) {
    // The backend expects an array of histories, so wrap single history in array
    const procedureId = data.irm_procedures_id;
    const response = await this.apiClient.post(`/api/v1/ivf/procedure/${procedureId}/histories`, [data]);
    // Backend returns { statusCode, flag, message, data: [array of created histories] }
    // Extract the first (and only) created history and return it in the same format
    if (response?.data && Array.isArray(response.data)) {
      return {
        ...response,
        data: response.data[0]
      };
    }
    return response;
  }

  async updateProcedureHistory(historyId, data) {
    return this.apiClient.patch(`/api/v1/ivf/procedure-history/${historyId}`, data);
  }

  async deleteProcedureHistory(historyId) {
    return this.apiClient.delete(`/api/v1/ivf/procedure-history/${historyId}`);
  }

  async updateProcedure(procedureId, data) {
    return this.apiClient.patch(`/api/v1/ivf/procedure/${procedureId}`, data);
  }

  async saveProcedureDetail(procedureId, data) {
    return this.apiClient.post(`/api/v1/ivf/procedure/${procedureId}/details`, data);
  }

  async updateProcedureDetail(detailId, data) {
    return this.apiClient.patch(`/api/v1/ivf/procedure-detail/${detailId}`, data);
  }

  async deleteProcedure(procedureId) {
    return this.apiClient.delete(`/api/v1/ivf/procedure/${procedureId}`);
  }

  async searchProcedures(params = {}) {
    const query = new URLSearchParams();
    if (params.procedure_code) query.append('procedure_code', params.procedure_code);
    if (params.patient_code) query.append('patient_code', params.patient_code);
    if (params.status) query.append('status', params.status);
    if (params.procedure_type) query.append('procedure_type', params.procedure_type);
    if (params.procedure_sub_type) query.append('procedure_sub_type', params.procedure_sub_type);
    if (params.date_from) query.append('date_from', params.date_from);
    if (params.date_to) query.append('date_to', params.date_to);
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    const qs = query.toString();
    return this.apiClient.get(`/api/v1/ivf/procedures/search${qs ? `?${qs}` : ''}`);
  }

  // Dashboard widget — procedure counts (total + type-wise + status-wise +
  // full type×status matrix) for a date window.
  async getProcedureDashboardStats({ date_from, date_to }) {
    const query = new URLSearchParams({ date_from, date_to });
    return this.apiClient.get(`/api/v1/ivf/procedures/dashboard/stats?${query.toString()}`);
  }

  async getUsgs(procedureId) {
    return this.apiClient.get(`/api/v1/ivf/procedure/${procedureId}/usg`);
  }

  async createUsg(procedureId, data) {
    return this.apiClient.post(`/api/v1/ivf/procedure/${procedureId}/usg`, data);
  }

  async updateUsg(usgId, data) {
    return this.apiClient.patch(`/api/v1/ivf/procedure-usg/${usgId}`, data);
  }

  async deleteUsg(usgId) {
    return this.apiClient.delete(`/api/v1/ivf/procedure-usg/${usgId}`);
  }

  // ── Folliculometry ────────────────────────────────────────────────
  async getFolliculometryParamGroup() {
    return this.apiClient.get('/api/v1/ivf/folliculometry/params');
  }

  async getFolliculometryDrugMaster() {
    return this.apiClient.get('/api/v1/ivf/folliculometry/drug-master');
  }

  async getEmbroTransferParamGroup() {
    return this.apiClient.get('/api/v1/ivf/embro-transfer/params');
  }

  async getFolliculometries(procedureId) {
    return this.apiClient.get(`/api/v1/ivf/procedure/${procedureId}/folliculometry`);
  }

  async createFolliculometry(procedureId, data) {
    return this.apiClient.post(`/api/v1/ivf/procedure/${procedureId}/folliculometry`, data);
  }

  async updateFolliculometryStatus(folliculometryId, status) {
    return this.apiClient.patch(`/api/v1/ivf/folliculometry/${folliculometryId}/status`, { status });
  }

  async deleteFolliculometry(folliculometryId) {
    return this.apiClient.delete(`/api/v1/ivf/folliculometry/${folliculometryId}`);
  }

  // ── Folliculometry Days ───────────────────────────────────────────
  async getFolliculometryDays(folliculometryId) {
    return this.apiClient.get(`/api/v1/ivf/folliculometry/${folliculometryId}/days`);
  }

  async createFolliculometryDay(folliculometryId, data) {
    return this.apiClient.post(`/api/v1/ivf/folliculometry/${folliculometryId}/days`, data);
  }

  async updateFolliculometryDay(dayId, data) {
    return this.apiClient.patch(`/api/v1/ivf/folliculometry-day/${dayId}`, data);
  }

  async deleteFolliculometryDay(dayId) {
    return this.apiClient.delete(`/api/v1/ivf/folliculometry-day/${dayId}`);
  }

  // ── Oocyte Collection ─────────────────────────────────────────────
  async getOocyteCollections(folliculometryId) {
    return this.apiClient.get(`/api/v1/ivf/folliculometry/${folliculometryId}/oocyte-collections`);
  }

  async createOocyteCollection(folliculometryId, data) {
    return this.apiClient.post(`/api/v1/ivf/folliculometry/${folliculometryId}/oocyte-collections`, data);
  }

  async updateOocyteCollection(id, data) {
    return this.apiClient.patch(`/api/v1/ivf/oocyte-collection/${id}`, data);
  }

  async deleteOocyteCollection(id) {
    return this.apiClient.delete(`/api/v1/ivf/oocyte-collection/${id}`);
  }

  // ── Procedure Donor ───────────────────────────────────────────────
  async attachDonorToProcedure(procedureId, donor_contact_id, donor_type) {
    return this.apiClient.post(`/api/v1/ivf/procedures/${procedureId}/attach-donor`, { donor_contact_id, donor_type });
  }

  async getProcedureDonor(procedureId, donor_type) {
    const params = donor_type ? { donor_type } : {};
    // "No donor attached yet" (404) is an expected, normal response here —
    // not an error worth popping a global alert over.
    return this.apiClient.get(`/api/v1/ivf/procedures/${procedureId}/donor`, { params, silent: true });
  }

  async detachDonorFromProcedure(procedureId, donor_type) {
    return this.apiClient.delete(`/api/v1/ivf/procedures/${procedureId}/detach-donor`, { params: { donor_type } });
  }

  // ── Lab Performance ───────────────────────────────────────────────
  async createLabPerformance(procedureId, data) {
    return this.apiClient.post(`/api/v1/ivf/procedure/${procedureId}/lab-performance`, data);
  }

  async getLabPerformance(procedureId) {
    return this.apiClient.get(`/api/v1/ivf/procedure/${procedureId}/lab-performance`);
  }

  async updateLabPerformance(labPerformanceId, data) {
    return this.apiClient.patch(`/api/v1/ivf/lab-performance/${labPerformanceId}`, data);
  }

  async deleteLabPerformance(labPerformanceId) {
    return this.apiClient.delete(`/api/v1/ivf/lab-performance/${labPerformanceId}`);
  }

  async createLabPerformanceRecord(labPerformanceId, data) {
    return this.apiClient.post(`/api/v1/ivf/lab-performance/${labPerformanceId}/records`, data);
  }

  async getLabPerformanceRecords(labPerformanceId) {
    return this.apiClient.get(`/api/v1/ivf/lab-performance/${labPerformanceId}/records`);
  }

  async updateLabPerformanceRecord(recordId, data) {
    return this.apiClient.patch(`/api/v1/ivf/lab-performance-record/${recordId}`, data);
  }

  async deleteLabPerformanceRecord(recordId) {
    return this.apiClient.delete(`/api/v1/ivf/lab-performance-record/${recordId}`);
  }

  async upsertLabPerformanceRecords(labPerformanceId, data) {
    return this.apiClient.post(`/api/v1/ivf/lab-performance/${labPerformanceId}/upsert-records`, data);
  }

  // ── Andrology Report Files ────────────────────────────────────────
  async uploadAndrologyReportFile(labPerformanceId, formData) {
    return this.apiClient.post(`/api/v1/ivf/lab-performance/${labPerformanceId}/andrology-files`, formData);
  }

  async getAndrologyReportFiles(labPerformanceId, reportType) {
    const query = reportType ? `?report_type=${reportType}` : '';
    return this.apiClient.get(`/api/v1/ivf/lab-performance/${labPerformanceId}/andrology-files${query}`);
  }
}
